import React from 'react';
import Title from './Title';
import PropTypes from 'prop-types';
import ContentType, { typeDisplayMap } from './ContentType';
import styles from './ContentCard.module.less';
import Dicon from '../Dicon';
import clsx from 'clsx';
import { Remarkable } from 'remarkable';

const ContentCard = React.forwardRef((props, ref) => {
  const {
    id,
    type,
    href,
    grade = '',
    subject = '',
    isPremium = false,
    isEL = false,
    isNew = false,
    isAdminOnly = false,
    withinGrid = false,
    className,
    title,
    description,
    isInteractive = false,
    children,
    ...otherProps
  } = props;

  const md = new Remarkable({ html: true });

  const defaultedDescription = description === null ? "" : description;

  const hasSmallImage =
    ['worksheet', 'guided-lesson', 'lesson-plan', 'workbook'].indexOf(type) >=
    0;

  // Exercises (aka skill builders) are a weird shape and need to be scaled, so a higher resolution is required
  const imageWidth = type === 'skill-builder' ? 320 : hasSmallImage ? 184 : 230;

  let rating, image, checkbox, collectible;
  React.Children.forEach(children, child => {
    if (!child) return; // ignore null
    if (!child.type || !child.type.displayName) {
      console.error('Unexpected child', child);
      return;
    }

    switch (child.type.displayName) {
      case 'StarRating':
        rating = <div className={styles.ratingHolder}>{child}</div>;
        break;
      case 'Image':
        // Default image alt to equal the title
        image = React.cloneElement(child, {
          alt: child.props.alt || title,
          displayWidth: imageWidth,
          // This is a hack to force the image to scale via the CDN
          naturalWidth: imageWidth + 1,
        });
        break;
      case 'ContentCardCheckbox':
        checkbox = child;
        break;
      case 'ContentCardCollectible':
        collectible = child;
        break;
      default:
        console.error('Unexpected child: ', child.type.displayName);
    }
  });

  // Fall back to using the content type icon
  if (!image) {
    image = <Dicon name={type} />;
  }

  return (
    <div
      className={clsx(styles[className], styles.card, {
        [styles.grid]: withinGrid,
        [styles.newContent]: isNew,
        [styles.adminOnly]: isAdminOnly,
      })}
      {...otherProps}
    >
      <a
        className={styles.link}
        href={href}
        data-id={id}
        data-type={type}
        ref={ref}
      >
        <div className={styles.front}>
          <div
            className={clsx(styles.image, { [styles.narrow]: hasSmallImage })}
          >
            {image}
          </div>
          <div className={styles.titleArea}>
            <Title withinGrid={withinGrid}>{title}</Title>
            <ContentType
              isPremium={isPremium}
              isEL={isEL}
              isInteractive={isInteractive}
              type={type}
              withinGrid={withinGrid}
            />
            {rating}
          </div>
        </div>
      </a>
      <div className={styles.hoverSection}>
        <Title>{title}</Title>
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: md.renderInline(defaultedDescription) }} />
        <div className={styles.categories}>
          {grade && <div>{grade}</div>}
          {subject && <div>{subject}</div>}
        </div>
        <ContentType
          isPremium={isPremium}
          isEL={isEL}
          isInteractive={isInteractive}
          type={type}
          withinGrid={withinGrid}
          inHoverSection={true}/>
        {rating}
      </div>
      {checkbox}
      {collectible}
    </div>
  );
});
ContentCard.displayName = 'ContentCard';
ContentCard.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.keys(typeDisplayMap)).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  /** e.g. "/worksheet/article/blah/" */
  href: PropTypes.string.isRequired,
  /** Grade display name (e.g. "1st Grade") */
  grade: PropTypes.string,
  /** Subject display name (e.g. "Reading & Writing") */
  subject: PropTypes.string,
  /** Set to true if only premium users are allowed to use this content */
  isPremium: PropTypes.bool,
  isNew: PropTypes.bool,
  isAdminOnly: PropTypes.bool,
  withinGrid: PropTypes.bool,
  isEL: PropTypes.bool,
  /** Set to true if this is a published active interactive worksheet and display animated sparkle on content card */
  isInteractive: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  collections: PropTypes.object, // change this to array of collection objects instead
};

export default ContentCard;
